import { type PropsWithChildren, type MouseEvent, useCallback } from 'react';
import { Link, type LinkProps } from '@geberit/gdds';

// Utils
import type { ContentProps } from './ContentProps';
import { phoneLink } from './trackingActions';
import { useTracking } from 'utils/hooks/useTracking';
import { useGdds } from 'utils/hooks/use-gdds';
import { useTracking as useGA4Tracking } from 'utils/tracking/track';

function renderLinkContent(children, content) {
  if (children !== null) {
    return children;
  }

  return content.text;
}

export type PhoneLinkProps = {
  content: ContentProps;
  className?: string;
  tracking?(e: MouseEvent<HTMLAnchorElement>): void;
  hasArrow?: boolean;
  alignByContent?: LinkProps['alignByContent'];
  stylingType?: LinkProps['stylingType'];
  standardFontSize?: boolean;
};

export function PhoneLink({
  content,
  children,
  className = '',
  tracking,
  stylingType,
  hasArrow,
  alignByContent,
  standardFontSize = true,
}: PropsWithChildren<PhoneLinkProps>) {
  const isGdds = useGdds();
  const track = useTracking();
  const { trackEvent } = useGA4Tracking();

  const handleTracking = useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => {
      if (!tracking) {
        const href = e.currentTarget.href;
        track.trackEvent(phoneLink(href));
        trackEvent({
          event: 'click',
          click_intent: 'contact',
          click_element: 'phone',
          click_text: href.replace(/^tel:/, ''),
        });
      } else {
        tracking(e);
      }
    },
    [track, trackEvent, tracking],
  );

  if (isGdds) {
    return (
      <Link
        stylingType={stylingType || 'secondary'}
        text={renderLinkContent(children, content)}
        target={content.target}
        onClick={handleTracking}
        hasArrow={hasArrow}
        alignByContent={alignByContent}
        className={className}
        windowTarget="_blank"
        standardFontSize={standardFontSize}
      />
    );
  }

  return (
    <a
      href={content.target}
      className={className}
      onClick={handleTracking}
      aria-label={content.text || content.target || undefined}
      target="_blank"
      rel="noreferrer"
    >
      {renderLinkContent(children, content)}
    </a>
  );
}
